import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { deviceService, enumService, tenantService } from '../services/api';
import { CreateDeviceModal, EditDeviceModal, AssignDeviceModal, UnassignConfirmModal } from '../components/DeviceModals';

const Devices = () => {
  const [searchParams] = useSearchParams();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', status: 'all', platformType: '', city: '', tenant: searchParams.get('tenantId') || '', iotStatus: '' });
  const [sorting, setSorting] = useState({ sortBy: 'created_at', sortOrder: 'desc' });
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Modals state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editDevice, setEditDevice] = useState(null);
  const [assignDevice, setAssignDevice] = useState(null);
  const [unassignDevice, setUnassignDevice] = useState(null);
  
  // Enum options from API
  const [enums, setEnums] = useState({});
  const [tenants, setTenants] = useState([]);

  // Fetch enums on mount
  useEffect(() => {
    const fetchEnums = async () => {
      try {
        const response = await enumService.getAll();
        setEnums(response.enums || {});
      } catch (err) {
        console.error('Error fetching enums:', err);
      }
    };
    const fetchTenants = async () => {
      try {
        const response = await tenantService.getAll({ pageSize: 100 });
        setTenants(response.data || []);
      } catch (err) {
        console.error('Error fetching tenants:', err);
      }
    };
    fetchEnums();
    fetchTenants();
  }, []);

  const fetchDevices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await deviceService.getAll({
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: filters.search,
        status: filters.status,
        platformType: filters.platformType,
        city: filters.city,
        tenant: filters.tenant,
        iotStatus: filters.iotStatus,
        sortBy: sorting.sortBy,
        sortOrder: sorting.sortOrder,
      });
      
      setDevices(response.data || []);
      if (response.pagination) {
        setPagination(prev => ({ ...prev, ...response.pagination }));
      }
    } catch (err) {
      console.error('Error fetching devices:', err);
      setError('Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters, sorting]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => {
        setMessage(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  const handleSearch = (e) => {
    setFilters({ ...filters, search: e.target.value });
    setPagination({ ...pagination, page: 1 });
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
    setPagination({ ...pagination, page: 1 });
  };

  const handleSort = (field) => {
    setSorting(prev => ({
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (field) => {
    if (sorting.sortBy !== field) return '↕';
    return sorting.sortOrder === 'asc' ? '↑' : '↓';
  };

  const handleUnassign = async (device) => {
    setActionLoading(device.id);
    try {
      await deviceService.unassignTenant(device.id);
      setMessage(`Device "${device.nodeName}" returned to inventory`);
      setUnassignDevice(null);
      fetchDevices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unassign device');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const getStatusBadge = (device) => {
    if (device.tenantId) {
      return <span className="status-badge status-active">🟢 Assigned</span>;
    }
    return <span className="status-badge status-pending">🟡 Inventory</span>;
  };

  const getIotStatusBadge = (device) => {
    if (device.iotRegistered) {
      return <span className="status-badge status-active">🟢 Registered</span>;
    }
    return <span className="status-badge status-disabled">⚪ Pending</span>;
  };

  const getPlatformBadge = (platformType) => {
    const platformLabels = {
      'in_cab': '🚕 In-Cab',
      'taxi_top': '🚖 Taxi Top',
      'in_flight': '✈️ In-Flight',
      'bus_stop': '🚏 Bus Stop',
      'billboard': '📺 Billboard',
      'transit_tv': '📺 Transit TV',
      'my_devices': '📱 My Devices',
    };
    return <span className="platform-badge">{platformLabels[platformType] || platformType}</span>;
  };

  const getHardwareIdPreview = (device) => {
    const truncate = (str, len = 8) => str.length > len ? `${str.substring(0, len)}...` : str;
    if (device.imei) return `IMEI: ${truncate(device.imei)}`;
    if (device.serialNumber) return `SN: ${truncate(device.serialNumber)}`;
    if (device.widevineId) return `WV: ${truncate(device.widevineId)}`;
    return '—';
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>📱 Edge Nodes Devices</h1>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
          + Add Device
        </button>
      </div>

      {message && (
        <div className="alert alert-success">✅ {message}</div>
      )}
      {error && (
        <div className="alert alert-error">❌ {error}</div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Search by name, IMEI, serial..."
            value={filters.search}
            onChange={handleSearch}
          />
        </div>
        <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
          <option value="all">All Devices</option>
          <option value="inventory">📦 In Inventory</option>
          <option value="assigned">✅ Assigned</option>
        </select>
        <select value={filters.platformType} onChange={(e) => handleFilterChange('platformType', e.target.value)}>
          <option value="">All Platforms</option>
          {enums.ad_platform_type?.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select value={filters.city} onChange={(e) => handleFilterChange('city', e.target.value)}>
          <option value="">All Cities</option>
          {enums.city?.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select value={filters.iotStatus} onChange={(e) => handleFilterChange('iotStatus', e.target.value)}>
          <option value="">All IoT Status</option>
          <option value="registered">🟢 Registered</option>
          <option value="pending">⚪ Pending</option>
        </select>
        <select value={filters.tenant} onChange={(e) => handleFilterChange('tenant', e.target.value)}>
          <option value="">All Tenants</option>
          {tenants.map(tenant => (
            <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
          ))}
        </select>
      </div>

      {/* Device Table */}
      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('node_name')}>
                    Device {getSortIcon('node_name')}
                  </th>
                  <th>Hardware ID</th>
                  <th>Status</th>
                  <th className="sortable" onClick={() => handleSort('tenant_name')}>
                    Tenant {getSortIcon('tenant_name')}
                  </th>
                  <th>IoT</th>
                  <th className="sortable" onClick={() => handleSort('node_home_geo_city')}>
                    City {getSortIcon('node_home_geo_city')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('created_at')}>
                    Created {getSortIcon('created_at')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {devices.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="empty-state">
                      No devices found. <button onClick={() => setShowCreateModal(true)} className="link-button">Add your first device →</button>
                    </td>
                  </tr>
                ) : (
                  devices.map((device) => (
                    <tr key={device.id}>
                      <td className="user-cell">
                        <div className="user-avatar" aria-label={`Avatar for ${device.nodeName}`}>
                          📱
                        </div>
                        <div className="user-info">
                          <div className="user-name">
                            <Link to={`/devices/${device.id}`}>{device.nodeName}</Link>
                          </div>
                          <div className="user-email">
                            {getPlatformBadge(device.platformType)}
                            <span style={{ marginLeft: '0.5rem', color: '#666' }}>
                              {device.geoMobility === 'mobile' ? '🚗' : '📍'} {device.aspectRatio}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {getHardwareIdPreview(device)}
                      </td>
                      <td>{getStatusBadge(device)}</td>
                      <td>
                        {device.tenantName ? (
                          <Link to={`/tenants/${device.tenantId}`}>{device.tenantName}</Link>
                        ) : (
                          <span style={{ color: '#999' }}>—</span>
                        )}
                      </td>
                      <td>{getIotStatusBadge(device)}</td>
                      <td style={{ textTransform: 'capitalize' }}>{device.city || '—'}</td>
                      <td>{formatDate(device.createdAt)}</td>
                      <td>
                        <div className="actions-dropdown">
                          <button className="btn btn-secondary btn-sm">
                            {actionLoading === device.id ? '...' : '⋮'}
                          </button>
                          <div className="dropdown-menu">
                            <Link to={`/devices/${device.id}`} className="dropdown-item">
                              👁 View Details
                            </Link>
                            <button className="dropdown-item" onClick={() => setEditDevice(device)}>
                              ✏️ Edit Device
                            </button>
                            {device.tenantId ? (
                              <button 
                                className="dropdown-item text-danger" 
                                onClick={() => setUnassignDevice(device)}
                              >
                                📦 Return to Inventory
                              </button>
                            ) : (
                              <button 
                                className="dropdown-item text-success" 
                                onClick={() => setAssignDevice(device)}
                              >
                                🔗 Assign to Tenant
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalCount > 0 && (
            <div className="pagination">
              <span>Showing {((pagination.page - 1) * pagination.pageSize) + 1}-{Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} devices</span>
              <div className="pagination-buttons">
                <button 
                  disabled={pagination.page === 1}
                  onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                >
                  ← Previous
                </button>
                <button 
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Device Modal */}
      {showCreateModal && (
        <CreateDeviceModal
          enums={enums}
          onClose={() => setShowCreateModal(false)}
          onSuccess={(msg) => { setMessage(msg); setShowCreateModal(false); fetchDevices(); }}
          onError={setError}
        />
      )}

      {/* Edit Device Modal */}
      {editDevice && (
        <EditDeviceModal
          device={editDevice}
          enums={enums}
          onClose={() => setEditDevice(null)}
          onSuccess={(msg) => { setMessage(msg); setEditDevice(null); fetchDevices(); }}
          onError={setError}
        />
      )}

      {/* Assign Device Modal */}
      {assignDevice && (
        <AssignDeviceModal
          device={assignDevice}
          tenants={tenants}
          onClose={() => setAssignDevice(null)}
          onSuccess={(msg) => { setMessage(msg); setAssignDevice(null); fetchDevices(); }}
          onError={setError}
        />
      )}

      {/* Unassign Confirmation Modal */}
      {unassignDevice && (
        <UnassignConfirmModal
          device={unassignDevice}
          onClose={() => setUnassignDevice(null)}
          onConfirm={() => handleUnassign(unassignDevice)}
          loading={actionLoading === unassignDevice.id}
        />
      )}
    </div>
  );
};

export default Devices;
